<template>
  <v-card outlined class="pending-order-card">
    <v-card-text class="pa-4">
      <div class="pending-header">
        <div>
          <div class="section-title">Pending Orders</div>
          <div class="section-subtitle">
            {{ orders.length }} open order{{ orders.length === 1 ? "" : "s" }}
          </div>
        </div>

        <DateTimePicker
          defaultRange="since30DaysAgo"
          :range="true"
          v-model="dateTime"
          @submit="getAll()"
        />
      </div>

      <v-skeleton-loader
        v-if="isLoading"
        type="list-item-two-line, list-item-two-line, list-item-two-line"
        class="mt-4"
      />

      <div v-else-if="orders.length > 0" class="order-list mt-4">
        <div
          v-for="order in orders"
          :key="order.id"
          class="order-row"
        >
          <div class="order-main">
            <div class="order-avatar">
              {{ getCustomerInitial(order) }}
            </div>

            <div class="order-info">
              <div class="order-title">
                {{ getCustomerName(order) }}

                <v-chip
                  v-if="order.isRemoteOrder || isRepairNestOrder(order)"
                  small
                  class="ml-1 order-chip"
                  :color="isRepairNestOrder(order) ? 'secondary' : 'success'"
                  :dark="isRepairNestOrder(order)"
                  :outlined="!isRepairNestOrder(order)"
                >
                  {{ isRepairNestOrder(order) ? "RepairNest" : "Online" }}
                </v-chip>

                <v-chip
                  v-if="order.isChocoOrder"
                  small
                  class="ml-1 order-chip"
                  color="indigo lighten-5"
                  text-color="indigo darken-3"
                >
                  CHOCO
                </v-chip>

                <v-chip
                  v-if="!order.Customer && order.isRemoteOrder"
                  small
                  class="ml-1 order-chip"
                  color="blue lighten-5"
                  text-color="blue darken-3"
                >
                  Unknown
                </v-chip>
              </div>

              <div
                v-if="!order.Customer && order.metadata && order.metadata.phoneNumber"
                class="order-phone"
              >
                {{ order.metadata.phoneNumber }}
              </div>

              <div class="order-meta">
                {{ getOrderReference(order) }}
                <span class="dot">•</span>
                {{ order.date | moment("from", "now") }}
              </div>
            </div>
          </div>

          <div class="order-side">
            <div class="order-total">
              {{ order.total | currency }}
            </div>

            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list dense class="menu-button">
                <v-list-item @click="viewOrder(order)">
                  <v-list-item-title>View Order</v-list-item-title>
                </v-list-item>

                <OrderStatus
                  v-if="!order.isRemoteOrder"
                  :order="order"
                  @submit="getAll()"
                />

                <v-divider />

                <v-list-item @click="deleteOrder(order)">
                  <v-list-item-title class="red--text">
                    Delete
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <lottie
          :height="160"
          :width="160"
          :options="defaultOptions"
          v-on:animCreated="handleAnimation"
        />

        <div class="empty-title">No pending orders</div>
        <div class="empty-subtitle">Everything is up to date.</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import orderService from "@/modules/Order/service";
import OrderStatus from "@/modules/Order/components/OrderStatus";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/empty_box.json";
const DateTimePicker = () => import("@/components/DateTimePicker");
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "PendingOrder",

  components: {
    OrderStatus,
    Lottie,
    DateTimePicker,
  },

  data() {
    return {
      dateTime: {
        start: this.$moment().subtract(30, "days").startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      orders: [],
      isLoading: true,
      defaultOptions: {
        loop: true,
        autoplay: true,
        animationData: animationData.default,
      },
    };
  },

  mounted() {
    this.getAll();
  },

  methods: {
    handleAnimation(anim) {
      this.anim = anim;
    },

    getCustomerName(order) {
      const retailer = order.retailer || (order.metadata && order.metadata.retailer);
      if (retailer) return retailer.locationName || retailer.businessName || retailer.name || "RepairNest retailer";
      if (order.Customer && order.Customer.name) {
        return order.Customer.name;
      }

      if (order.metadata && order.metadata.name) {
        return order.metadata.name;
      }

      return "Unknown Customer";
    },

    getCustomerInitial(order) {
      const name = this.getCustomerName(order);
      return name ? name.charAt(0).toUpperCase() : "?";
    },

    isRepairNestOrder(order) {
      return !!(order && (order.source === "repairnest" || order.sourceLabel === "RepairNest" || order.displayOrderNumber || (order.metadata && order.metadata.source === "repairnest")));
    },

    getOrderReference(order) {
      if (this.isRepairNestOrder(order)) return order.displayOrderNumber || order.marketOrderNumber || "Order from RepairNest";
      return `Order #${order.localId}`;
    },

    deleteOrder(order) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          orderService.delete(order.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Order has been deleted.", "success");
              this.getAll();
            }
          });
        }
      });
    },

    viewOrder(order) {
      const hashId = hashids.encode(order.id);

      const routeData = this.$router.resolve({
        path: `/global/order/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    async getAll() {
      this.isLoading = true;

      const where = {
        // DateTimePicker emits date-only values. Keep them date-only so the
        // API can treat `end` as an inclusive calendar date. Converting the
        // end value to ISO here turns it into midnight at the start of that
        // day, which makes the "Today" range empty.
        start: this.dateTime.start,
        end: this.dateTime.end,
        pending: true,
      };

      try {
        const result = await orderService.getAll(where);
        this.orders = result.data || [];
      } catch (e) {
        console.error("getAll orders failed", e);
        this.orders = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.pending-order-card {
  border-radius: 20px;
  background: #ffffff;
}

.pending-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.order-list {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.order-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
}

.order-row:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.order-main {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;
}

.order-avatar {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 14px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.order-info {
  min-width: 0;
}

.order-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.order-phone {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.order-meta {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.dot {
  margin: 0 6px;
  color: #94a3b8;
}

.order-side {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-total {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
}

.order-chip {
  font-weight: 700;
}

.empty-state {
  text-align: center;
  padding: 24px 0;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .order-row {
    align-items: flex-start;
  }

  .order-side {
    flex-direction: column;
    align-items: flex-end;
  }
}
</style>
